import type { ReactNode } from 'react';
import {
  FaCog,
  FaSignInAlt,
  FaChevronRight,
  FaHome,
  FaUserCog,
  FaUsers,
  FaComments,
  FaPenFancy,
  FaChartBar,
} from 'react-icons/fa';
import { Navigate } from 'react-router-dom';
import { LoginPage, RegisterPage } from '@/features/auth';
import { lazy } from 'react';

const LandingPage = lazy(() => import('../pages/landing'));
// TODO: 「訂單管理」暫時隱藏，連同下方 /orders 路由區塊一起恢復時取消註解。
// const OrderListPage = lazy(() =>
//   import('@/features/orders').then((m) => ({ default: m.OrderListPage }))
// );
// const ImportOrdersPage = lazy(() =>
//   import('@/features/orders').then((m) => ({ default: m.ImportOrdersPage }))
// );
// const ExportOrdersPage = lazy(() =>
//   import('@/features/orders').then((m) => ({ default: m.ExportOrdersPage }))
// );
const UsersPage = lazy(() => import('@/features/users').then((m) => ({ default: m.UsersPage })));
const AdminSettingsPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.AdminSettingsPage }))
);
const AiChatPage = lazy(() =>
  import('@/features/ai-chat').then((m) => ({ default: m.AiChatPage }))
);
const CompanySettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.CompanySettingsPage }))
);
const BrandSettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.BrandSettingsPage }))
);
const NumberSettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.NumberSettingsPage }))
);
const LogisticsSettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.LogisticsSettingsPage }))
);
const PaymentSettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.PaymentSettingsPage }))
);
const SocialAnalysisPage = lazy(() =>
  import('@/features/social-analysis').then((m) => ({ default: m.SocialAnalysisPage }))
);
const SocialAnalysisDemoPage = lazy(() => import('../pages/social-analysis-demo'));
const TermsPage = lazy(() => import('../pages/terms'));
const AiPublishPage = lazy(() =>
  import('@/features/ai-publish').then((m) => ({ default: m.AiPublishPage }))
);
const UsagePage = lazy(() => import('@/features/usage').then((m) => ({ default: m.UsagePage })));
const ManageSubscriptionPage = lazy(() =>
  import('@/features/subscription').then((m) => ({ default: m.ManageSubscriptionPage }))
);
const SubscriptionSuccessPage = lazy(() =>
  import('@/features/subscription').then((m) => ({ default: m.SubscriptionSuccessPage }))
);
const SubscriptionCancelPage = lazy(() =>
  import('@/features/subscription').then((m) => ({ default: m.SubscriptionCancelPage }))
);

export interface RouteConfig {
  path: string;
  element?: ReactNode;
  title: string;
  icon?: ReactNode;
  showInMenu?: boolean;
  showSidebar?: boolean; // false = header only, no sidebar
  showHeader?: boolean; // false = no header at all (like login page)
  roles?: string[];
  allowedEmails?: string[]; // 若設定，僅這些 email 可進入（不分大小寫）
  requiresBilling?: boolean; // 需金流總開關（VITE_BILLING_ENABLE）開啟才可進入
  children?: RouteConfig[];
  expandable?: boolean;
  isPublic?: boolean; // Allow guest access without authentication
}

export const routeConfig: RouteConfig[] = [
  {
    path: '/',
    element: <LandingPage />,
    title: '首頁',
    icon: <FaHome />,
    showInMenu: false,
    showSidebar: false,
    showHeader: false,
    isPublic: true,
  },
  {
    path: '/terms',
    element: <TermsPage />,
    title: '使用條款與隱私說明',
    showInMenu: false,
    showSidebar: false,
    showHeader: false,
    isPublic: true,
  },
  {
    path: '/login',
    element: <LoginPage />,
    title: 'Login',
    icon: <FaSignInAlt />,
    showInMenu: false,
  },
  {
    path: '/register',
    element: <RegisterPage />,
    title: 'Register',
    showInMenu: false,
  },
  {
    path: '/ai-chat',
    element: <AiChatPage />,
    title: 'AI Chat',
    icon: <FaComments />,
    showInMenu: true,
    isPublic: false,
  },
  // /personality route removed (個資法/GDPR 考量，2026-05-27 下架)
  {
    path: '/social-analysis',
    element: <SocialAnalysisPage />,
    title: 'Social Intelligence',
    icon: <FaUsers />,
    showInMenu: true,
    isPublic: false,
  },
  {
    path: '/ai-publish',
    element: <AiPublishPage />,
    title: 'Threads Intelligence',
    icon: <FaPenFancy />,
    showInMenu: true,
    isPublic: false,
  },
  {
    path: '/usage',
    element: <UsagePage />,
    title: 'Usage Statistics',
    icon: <FaChartBar />,
    showInMenu: true,
    isPublic: false,
    roles: ['super_admin'],
  },
  {
    path: '/subscription',
    element: <ManageSubscriptionPage />,
    title: '訂閱管理',
    showInMenu: false,
    isPublic: false,
    requiresBilling: true,
  },
  {
    path: '/subscription/success',
    element: <SubscriptionSuccessPage />,
    title: '訂閱成功',
    showInMenu: false,
    isPublic: false,
    requiresBilling: true,
  },
  {
    path: '/subscription/cancel',
    element: <SubscriptionCancelPage />,
    title: '取消結帳',
    showInMenu: false,
    isPublic: false,
    requiresBilling: true,
  },
  {
    path: '/social-analysis-demo',
    element: <SocialAnalysisDemoPage />,
    title: 'Social Intelligence Demo',
    showInMenu: false,
    showSidebar: false,
    isPublic: true,
  },
  {
    path: '/admin',
    element: <Navigate to="/admin/users" replace />,
    title: '管理員',
    icon: <FaUserCog />,
    showInMenu: true,
    expandable: true,
    roles: ['admin'],
    children: [
      {
        path: '/admin/users',
        element: <UsersPage />,
        title: '使用者列表',
        icon: <FaChevronRight />,
        showInMenu: true,
        roles: ['admin'],
      },
      {
        path: '/admin/settings',
        element: <AdminSettingsPage />,
        title: '系統設定',
        icon: <FaChevronRight />,
        showInMenu: true,
        roles: ['admin'],
      },
    ],
  },
  // TODO: 「訂單管理」暫時隱藏（選單按鈕 + 路由皆停用）。要恢復時取消以下註解即可。
  // {
  //   path: '/orders',
  //   title: '訂單管理',
  //   icon: <FaShoppingCart />,
  //   showInMenu: true,
  //   expandable: true,
  //   roles: ['admin'],
  //   children: [
  //     {
  //       path: '/orders/list',
  //       element: <OrderListPage />,
  //       title: '訂單列表',
  //       icon: <FaChevronRight />,
  //       showInMenu: true,
  //       roles: ['admin'],
  //     },
  //     {
  //       path: '/orders/import',
  //       element: <ImportOrdersPage />,
  //       title: '匯入訂單',
  //       icon: <FaChevronRight />,
  //       showInMenu: true,
  //       roles: ['admin'],
  //     },
  //     {
  //       path: '/orders/export',
  //       element: <ExportOrdersPage />,
  //       title: '匯出訂單',
  //       icon: <FaChevronRight />,
  //       showInMenu: true,
  //       roles: ['admin'],
  //     },
  //   ],
  // },
  {
    path: '/settings',
    title: '網站設定',
    icon: <FaCog />,
    showInMenu: false,
    expandable: true,
    children: [
      {
        path: '/settings/company',
        element: <CompanySettingsPage />,
        title: '公司設定',
        icon: <FaChevronRight />,
        showInMenu: true,
      },
      {
        path: '/settings/brand',
        element: <BrandSettingsPage />,
        title: '品牌設定',
        icon: <FaChevronRight />,
        showInMenu: true,
      },
      {
        path: '/settings/number',
        element: <NumberSettingsPage />,
        title: '編號設定',
        icon: <FaChevronRight />,
        showInMenu: true,
      },
      {
        path: '/settings/logistics',
        element: <LogisticsSettingsPage />,
        title: '物流設定',
        icon: <FaChevronRight />,
        showInMenu: true,
      },
      {
        path: '/settings/payment',
        element: <PaymentSettingsPage />,
        title: '金流設定',
        icon: <FaChevronRight />,
        showInMenu: true,
      },
    ],
  },
];

export const getMenuItems = (userRole?: string, isSuperAdmin = false) => {
  return routeConfig.filter((route) => {
    if (!route.showInMenu) return false;
    if (!route.roles) return true;
    if (route.roles.includes('super_admin') && isSuperAdmin) return true;
    return !!userRole && route.roles.includes(userRole);
  });
};

export const getRouteByPath = (path: string): RouteConfig | undefined => {
  const search = (routes: RouteConfig[]): RouteConfig | undefined => {
    for (const route of routes) {
      if (route.path === path) return route;
      if (route.children) {
        const found = search(route.children);
        if (found) return found;
      }
    }
    return undefined;
  };
  return search(routeConfig);
};

// Flatten all routes (including nested child routes) for React Router configuration
// Only includes routes with an element
export const flattenRoutes = (routes: RouteConfig[] = routeConfig): RouteConfig[] => {
  const flattened: RouteConfig[] = [];

  const flatten = (routes: RouteConfig[]) => {
    routes.forEach((route) => {
      // Only add routes with an element
      if (route.element) {
        flattened.push({
          ...route,
          children: undefined,
        });
      }

      // If there are child routes, recursively add them
      if (route.children) {
        flatten(route.children);
      }
    });
  };

  flatten(routes);
  return flattened;
};
